import { supabase } from '../lib/supabase'
import type { SupportCenterAssistanceRef } from '../types/supportCenterAssistances'
import type {
  SupportCenterDemandType,
  SupportCenterReferralOrigin,
  SupportCenterTriageCatalogInput,
} from '../types/supportCenterTriage'
import { nowTimestampSaoPaulo } from '../utils/dateOnly'

function asAssistances(raw: unknown): SupportCenterAssistanceRef[] {
  if (!Array.isArray(raw)) return []

  const unique = new Map<string, SupportCenterAssistanceRef>()
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const relation = item as { assistance?: unknown }
    const assistanceRaw = Array.isArray(relation.assistance)
      ? relation.assistance[0]
      : relation.assistance
    if (!assistanceRaw || typeof assistanceRaw !== 'object') continue
    const assistance = assistanceRaw as {
      id?: string
      name?: string | null
      code?: string | null
      deleted_at?: string | null
    }
    const id = String(assistance.id ?? '').trim()
    if (!id || assistance.deleted_at) continue
    unique.set(id, {
      id,
      name: String(assistance.name ?? '').trim(),
      code: String(assistance.code ?? '').trim(),
    })
  }

  return [...unique.values()].sort((a, b) => {
    const byCode = a.code.localeCompare(b.code, 'pt-BR', { sensitivity: 'base' })
    if (byCode !== 0) return byCode
    return a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
  })
}

/** A-Z (pt-BR); "Outros" sempre por último */
function sortCatalogByName<T extends { name: string }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const aOutros = a.name.trim().toLocaleLowerCase('pt-BR') === 'outros'
    const bOutros = b.name.trim().toLocaleLowerCase('pt-BR') === 'outros'
    if (aOutros !== bOutros) return aOutros ? 1 : -1
    return a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
  })
}

function mapReferral(row: Record<string, unknown>): SupportCenterReferralOrigin {
  return {
    id: String(row.id),
    name: String(row.name ?? ''),
    requires_notes: Boolean(row.requires_notes),
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
    deleted_at: (row.deleted_at as string | null) ?? null,
    deleted_by: (row.deleted_by as string | null) ?? null,
    deleted_reason: (row.deleted_reason as string | null) ?? null,
  }
}

function mapDemand(row: Record<string, unknown>): SupportCenterDemandType {
  return {
    id: String(row.id),
    name: String(row.name ?? ''),
    requires_notes: Boolean(row.requires_notes),
    assistances: asAssistances(row.assistances),
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
    deleted_at: (row.deleted_at as string | null) ?? null,
    deleted_by: (row.deleted_by as string | null) ?? null,
    deleted_reason: (row.deleted_reason as string | null) ?? null,
  }
}

async function syncDemandAssistances(
  demandTypeId: string,
  assistanceIds: string[]
): Promise<void> {
  const unique = Array.from(new Set(assistanceIds.map((item) => item.trim()).filter(Boolean)))
  if (unique.length === 0) {
    throw new Error('Selecione ao menos uma assistência para exibir a demanda.')
  }

  const { error: deleteError } = await supabase
    .from('support_center_demand_type_assistances')
    .delete()
    .eq('demand_type_id', demandTypeId)

  if (deleteError) throw deleteError

  const { error: insertError } = await supabase
    .from('support_center_demand_type_assistances')
    .insert(unique.map((assistance_id) => ({ demand_type_id: demandTypeId, assistance_id })))

  if (insertError) throw insertError
}

export const supportCenterTriageCatalogService = {
  async listReferralOrigins(): Promise<SupportCenterReferralOrigin[]> {
    const { data, error } = await supabase
      .from('support_center_referral_origins')
      .select('*')
      .is('deleted_at', null)
      .order('name', { ascending: true })

    if (error) throw error
    return sortCatalogByName((data ?? []).map((row) => mapReferral(row as Record<string, unknown>)))
  },

  async listDemandTypes(): Promise<SupportCenterDemandType[]> {
    const { data, error } = await supabase
      .from('support_center_demand_types')
      .select(
        `
        *,
        assistances:support_center_demand_type_assistances(
          assistance:support_center_assistances!assistance_id(id, name, code, deleted_at)
        )
      `
      )
      .is('deleted_at', null)
      .order('name', { ascending: true })

    if (error) throw error
    return sortCatalogByName((data ?? []).map((row) => mapDemand(row as Record<string, unknown>)))
  },

  async upsertReferralOrigin(input: SupportCenterTriageCatalogInput): Promise<string> {
    const name = input.name.trim()
    if (!name) throw new Error('Informe o nome da opção.')

    const payload = {
      name,
      requires_notes: Boolean(input.requires_notes),
    }

    if (input.id) {
      const { error } = await supabase
        .from('support_center_referral_origins')
        .update(payload)
        .eq('id', input.id)
        .is('deleted_at', null)
      if (error) throw error
      return input.id
    }

    const { data, error } = await supabase
      .from('support_center_referral_origins')
      .insert(payload)
      .select('id')
      .single()
    if (error) throw error
    return String(data.id)
  },

  async upsertDemandType(input: SupportCenterTriageCatalogInput): Promise<string> {
    const name = input.name.trim()
    if (!name) throw new Error('Informe o nome da opção.')
    const assistanceIds = input.assistance_ids ?? []
    if (assistanceIds.length === 0) {
      throw new Error('Selecione ao menos uma assistência para exibir a demanda.')
    }

    const payload = {
      name,
      requires_notes: Boolean(input.requires_notes),
    }

    let demandTypeId = input.id
    if (demandTypeId) {
      const { error } = await supabase
        .from('support_center_demand_types')
        .update(payload)
        .eq('id', demandTypeId)
        .is('deleted_at', null)
      if (error) throw error
    } else {
      const { data, error } = await supabase
        .from('support_center_demand_types')
        .insert(payload)
        .select('id')
        .single()
      if (error) throw error
      demandTypeId = String(data.id)
    }

    await syncDemandAssistances(demandTypeId, assistanceIds)
    return String(demandTypeId)
  },

  async softDeleteReferralOrigin(id: string, actorId: string, reason: string): Promise<void> {
    const deleted_reason = reason.trim()
    if (!deleted_reason) throw new Error('Informe o motivo da exclusão.')

    const { error } = await supabase
      .from('support_center_referral_origins')
      .update({
        deleted_at: nowTimestampSaoPaulo(),
        deleted_by: actorId,
        deleted_reason,
      })
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
  },

  async softDeleteDemandType(id: string, actorId: string, reason: string): Promise<void> {
    const deleted_reason = reason.trim()
    if (!deleted_reason) throw new Error('Informe o motivo da exclusão.')

    const { error } = await supabase
      .from('support_center_demand_types')
      .update({
        deleted_at: nowTimestampSaoPaulo(),
        deleted_by: actorId,
        deleted_reason,
      })
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
  },
}
